import { Component, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { BeUtil, FeUtil } from '../../../hezky-routes';
import { updateName, updatePref } from '../../auth/state/auth.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { AuthorizationService as AUTHORIZATION } from '../../../hezky-routes';
import { Location } from '@angular/common';
interface TimeZone {
  name: string;
  value: string;
}
export const timezones: TimeZone[] = [
  { name: "Pacific Standard Time (PST)", value: "America/Los_Angeles" },
  { name: "Mountain Standard Time (MST)", value: "America/Denver" },
  { name: "Central Standard Time (CST)", value: "America/Chicago" },
  { name: "Eastern Standard Time (EST)", value: "America/New_York" },
  { name: "Greenwich Mean Time (GMT)", value: "Europe/London" },
  { name: "Central European Time (CET)", value: "Europe/Berlin" },
  { name: "India Standard Time (IST)", value: "Asia/Kolkata" },
  { name: "Japan Standard Time (JST)", value: "Asia/Tokyo" },
];

@UntilDestroy()
@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
})
export class UserProfileComponent implements OnInit {
  isEditing = false;
  oldPasswordVisible = false;
  newPasswordVisible = false;
  confirmPasswordVisible = false;
  apiCallLoader = { isSubmitLoading: false }
  profileForm!: FormGroup;
  passwordForm!: FormGroup;
  preferencesForm!: FormGroup;
  profileData: { firstName: string, lastName: string, email: string, contact?: string, role?: string } = {
      firstName: '',
      lastName: '',
      email: '',
    };
  timezones: TimeZone[] = timezones;

  constructor(
    protected readonly feUtil: FeUtil,
    protected readonly beUtil: BeUtil,
    protected _location: Location,
    private readonly authorization: AUTHORIZATION,
  ) { }

  ngOnInit() {
    this.profileData = {
      firstName: this.feUtil?.user_data?.first_name,
      lastName: this.feUtil?.user_data?.last_name,
      email: this.feUtil?.user_data?.email,
      contact: this.feUtil?.user_data?.contact,
      role: this.feUtil?.user_data?.userRoles?.role_name
    };
    this.initializeForms();
  }

  initializeForms() {
    this.profileForm = this.feUtil.fb.group({
      user_id: [this.feUtil.user_data.user_id],
      first_name: [this.profileData.firstName, [Validators.required]],
      last_name: [this.profileData.lastName, [Validators.required]]
    });
    this.passwordForm = this.feUtil.fb.group({
      email: [this.feUtil.user_data.email],
      oldPassword: ['', [Validators.required, Validators.maxLength(10)]],
      newPassword: ['', [Validators.required, Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]+$'), Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8)]]
    }, { validators: [this.passwordMatchValidator, this.oldNewPasswordValidator]});
    this.passwordForm.get('oldPassword')?.valueChanges.subscribe(() => {
      this.passwordForm.get('newPassword')?.updateValueAndValidity();
    });
    this.passwordForm.get('newPassword')?.valueChanges.subscribe(() => {
      const confirmPasswordControl = this.passwordForm.get('confirmPassword');
      if (confirmPasswordControl) {
        confirmPasswordControl.updateValueAndValidity();
      }
    });
    this.preferencesForm = this.feUtil.fb.group({
      user_id: [this.feUtil.user_data.user_id],
      tempPreference: [this.feUtil.temp_pref, Validators.required],
      timezone: [this.feUtil.time_zone, Validators.required]
    });
  }

  passwordMatchValidator(g: FormGroup): void {
    const newPassword = g.get('newPassword');
    const confirmPassword = g.get('confirmPassword');
    if (!newPassword || !confirmPassword) return;
    if (newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
    } else {
      confirmPassword.setErrors(null);
    }
  }

  oldNewPasswordValidator(g: FormGroup): void {
    const oldPassword = g.get('oldPassword');
    const newPassword = g.get('newPassword');
    if (!oldPassword || !newPassword) return;
    if (oldPassword.value && newPassword.value) {
      if (oldPassword.value === newPassword.value) {
        newPassword.setErrors({ sameAsOld: true });
      } else {
        const currentErrors = newPassword.errors;
        if (currentErrors) {
          delete currentErrors['sameAsOld'];
          if (Object.keys(currentErrors).length === 0) {
            newPassword.setErrors(null);
          } else {
            newPassword.setErrors(currentErrors);
          }
        }
      }
    }
  }

  onProfileSubmit(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }
    this.apiCallLoader.isSubmitLoading = true;
    const requestData = {...this.profileForm.value}
    delete requestData.user_id;
    this.beUtil.usersSvc.updateUser(requestData, this.feUtil.user_data.user_id.toString()).pipe(untilDestroyed(this),
      finalize(() => (this.apiCallLoader.isSubmitLoading = false)))
      .subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.feUtil.message.success('Profile Updated.', { nzDuration: 5000 });
            this.feUtil.store.dispatch(updateName({
              name: {
                first_name: this.profileForm.get('first_name')?.value,
                last_name: this.profileForm.get('last_name')?.value
              }
            }));
            this.isEditing = false;
            this.profileData = {
              firstName: this.profileForm.get('first_name')?.value,
              lastName: this.profileForm.get('last_name')?.value,
              email: this.feUtil.user_data.email,
              contact: this.feUtil.user_data.contact,
              role: this.feUtil.user_data.userRoles?.role_name
            };
          }
        }
      });
  }

  onPasswordSubmit(): void {
    if (this.passwordForm.invalid) return;
    const requestData = {
      old_password: this.passwordForm.get('oldPassword')?.value,
      password: this.passwordForm.get('newPassword')?.value,
    }
    this.apiCallLoader.isSubmitLoading = true
    this.beUtil.usersSvc.updateUser(requestData, this.feUtil.user_data.user_id.toString()).pipe(untilDestroyed(this),
      finalize(() => (this.apiCallLoader.isSubmitLoading = false))).subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.feUtil.message.success('Password has been changed successfully.', { nzDuration: 5000 });
            this.authorization.onLogout();
          } else {
            this.feUtil.message.error(response.message, { nzDuration: 5000 });
          }
        },
        error: (error: any) => console.log(error)
      });
  }

  onPreferencesSubmit() {
    if (!this.preferencesForm.valid) return
    this.apiCallLoader.isSubmitLoading = true
    const requestData = { ...this.preferencesForm.value }
    delete requestData.user_id;
    this.beUtil.usersSvc.updateUser(requestData, this.feUtil.user_data.user_id.toString()).pipe(untilDestroyed(this),
      finalize(() => (this.apiCallLoader.isSubmitLoading = false))).subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.feUtil.message.success('Settings updated successfully!', { nzDuration: 5000 });
            this.feUtil.store.dispatch(updatePref({
              preference: {
                temp_preference: this.preferencesForm.get('tempPreference')?.value,
                timezone: this.preferencesForm.get('timezone')?.value
              }
            }));
          }
        }
      });
  }
}